import React, { useCallback } from 'react';
import Button from '@/components/UI/Button';
import FormSelect from '@/components/UI/FormSelect';
import FormNumberInput from '@/components/UI/FormNumberInput';
import Modal from '@/components/UI/Modal';
import MultiSelect from '@/components/UI/MultiSelect';
import { showToast } from '@/components/Layout/Toast';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { EntityTypeUnion, PositionSizeRule } from '@einhaus/project314-types';

interface PositionSizeRuleModalProps {
    isOpen: boolean;
    onClose: () => void;
    portfolioId: string | undefined;
    editingRuleId: string | null;
    newRule: Partial<PositionSizeRule>;
    setNewRule: React.Dispatch<React.SetStateAction<Partial<PositionSizeRule>>>;
    rules: PositionSizeRule[];
    setRules: React.Dispatch<React.SetStateAction<PositionSizeRule[]>>;
}

const PositionSizeRuleModal: React.FC<PositionSizeRuleModalProps> = ({
    isOpen,
    onClose,
    portfolioId,
    editingRuleId,
    newRule,
    setNewRule,
    rules,
    setRules
}) => {
    const queryClient = useQueryClient();

    // Mutation for saving position size rule (add/edit)
    const savePositionSizeRuleMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });
            }
        }),
        {
            successMessage: 'Position size rule saved successfully',
            fallbackErrorMessage: 'Failed to save position size rule'
        }
    );

    const handleSavePositionSizeRule = useCallback(async () => {
        if (!portfolioId) return;

        // Ensure we have valid entity types
        if (!newRule.entityTypes || newRule.entityTypes.length === 0) {
            showToast('Please select at least one entity type', 'error');
            return;
        }

        const ruleId = editingRuleId ?? Date.now().toString();

        const rule = {
            type: newRule.type || 'fixed',
            entityTypes: newRule.entityTypes,
            value: newRule.value || 0,
            id: ruleId
        };

        const previousRules = rules;

        const updatedRules =
            editingRuleId == null ? [...previousRules, rule] : previousRules.map((existing) => (existing.id === ruleId ? rule : existing));

        // Optimistically update UI
        setRules(updatedRules);
        onClose();

        try {
            await savePositionSizeRuleMutation.mutateAsync({
                portfolioId: parseInt(portfolioId),
                settingType: 'positionSizeRule',
                value: rule
            });
        } catch {
            setRules(previousRules);
        }
    }, [portfolioId, editingRuleId, newRule, rules, savePositionSizeRuleMutation, onClose, setRules]);

    return (
        <Modal isOpen={isOpen} title={editingRuleId == null ? 'Add Position Size Rule' : 'Edit Position Size Rule'} onClose={onClose}>
            <div className="space-y-4">
                <div className="mb-4">
                    <FormSelect
                        id="positionSizeRuleType"
                        label="Rule Type"
                        options={[
                            { value: 'fixed', label: 'Fixed Position Size' },
                            { value: 'percentage', label: 'Percentage of Portfolio' },
                            { value: 'minimumDollar', label: 'Minimum Dollar Value' },
                            { value: 'maximumDollar', label: 'Maximum Dollar Value' },
                            { value: 'maximumRiskCapitalDollar', label: 'Maximum Risk Capital (Dollar)' },
                            { value: 'minimumRiskCapitalDollar', label: 'Minimum Risk Capital (Dollar)' },
                            { value: 'fixedRiskCapitalDollar', label: 'Fixed Risk Capital (Dollar)' },
                            { value: 'percentagePortfolioRiskCapital', label: 'Percentage of Portfolio Risk Capital' }
                        ]}
                        value={newRule.type || 'fixed'}
                        onChange={(e) =>
                            setNewRule({
                                ...newRule,
                                type: e.target.value as PositionSizeRule['type']
                            })
                        }
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" htmlFor="positionSizeEntityTypes">
                        Entity Types
                    </label>
                    <MultiSelect
                        isMulti
                        className="w-full"
                        id="positionSizeEntityTypes"
                        options={[
                            { value: 'Asset', label: 'Stock' },
                            { value: 'Cryptocurrency', label: 'Cryptocurrency' },
                            { value: 'AssetIndex', label: 'Index' }
                        ]}
                        placeholder="Select entity types..."
                        value={newRule.entityTypes?.map((type) => ({ value: type, label: type })) || []}
                        onChange={(selected) => {
                            if (Array.isArray(selected)) {
                                setNewRule({
                                    ...newRule,
                                    entityTypes: selected.map((opt) => opt.value as EntityTypeUnion['entityType'])
                                });
                            }
                        }}
                    />
                </div>
                <div className="mb-4">
                    <FormNumberInput
                        id="positionSizeRuleValue"
                        label="Value"
                        min={0}
                        value={newRule.value === undefined || newRule.value === '' ? null : Number(newRule.value)}
                        onValueChange={(value) =>
                            setNewRule({
                                ...newRule,
                                value: value ?? undefined
                            })
                        }
                    />
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                    <Button className="mr-2" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSavePositionSizeRule}>
                        {editingRuleId == null ? 'Add Rule' : 'Save Rule'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PositionSizeRuleModal;
